import { CommonModule } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import { Component, Input, Output, EventEmitter } from '@angular/core';

export interface TableColumn {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
}

/**
 * Sortable data table with configurable columns, row click, custom cell templates, and empty state.
 */
@Component({
  selector: 'es-data-table',
  imports: [CommonModule],
  template: `
    <div class="overflow-auto rounded-lg border border-[var(--border)]">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-[var(--bg-secondary)]">
            @for (col of columns; track col.key) {
              <th
                class="px-4 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider"
                [style.width]="col.width"
                [class.cursor-pointer]="col.sortable"
                (click)="col.sortable ? sort(col.key) : null"
              >
                {{ col.label }}
                @if (col.sortable && sortKey === col.key) {
                  <span class="ml-1">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                }
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of data; track trackBy ? trackBy(row) : $index) {
            <tr
              class="border-t border-[var(--border)] hover:bg-[var(--bg-card-hover)] transition-colors"
              [class.cursor-pointer]="clickable"
              (click)="clickable ? rowClicked.emit(row) : null"
            >
              @for (col of columns; track col.key) {
                <td class="px-4 py-3 text-[var(--text)]">
                  @if (cellTemplate) {
                    <ng-container *ngTemplateOutlet="cellTemplate; context: { $implicit: row, column: col }"></ng-container>
                  } @else {
                    {{ row[col.key] }}
                  }
                </td>
              }
            </tr>
          }
          @if (data.length === 0) {
            <tr>
              <td [attr.colspan]="columns.length" class="px-4 py-8 text-center text-[var(--text-secondary)]">
                {{ emptyMessage }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
})
export class DataTableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() data: Record<string, unknown>[] = [];
  @Input() clickable = false;
  @Input() emptyMessage = 'Keine Daten vorhanden';
  @Input() cellTemplate: TemplateRef<unknown> | null = null;
  @Input() trackBy?: (row: Record<string, unknown>) => string;
  @Output() rowClicked = new EventEmitter<Record<string, unknown>>();
  @Output() sorted = new EventEmitter<{ key: string; dir: 'asc' | 'desc' }>();

  sortKey: string | null = null;
  sortDir: 'asc' | 'desc' = 'asc';

  sort(key: string) {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.sorted.emit({ key, dir: this.sortDir });
  }
}
